import { Routes, Route, NavLink, useNavigate } from "react-router-dom";
import { Home, Tv, Film, Clapperboard, User, LogOut } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useState, useEffect } from "react";
import { initWebPush } from "@/services/push";

// Pages
import HomePage from "@/pages/app/HomePage";
import LiveTVPage from "@/pages/app/LiveTVPage";
import MoviesPage from "@/pages/app/MoviesPage";
import SeriesPage from "@/pages/app/SeriesPage";
import ProfilePage from "@/pages/app/ProfilePage";
import PlayerPage from "@/pages/app/PlayerPage";
import QrScanPage from "@/pages/app/QrScanPage";

const navItems = [
  { path: "/app", icon: Home, label: "Ana Səhifə", shortLabel: "Əsas", end: true },
  { path: "/app/live", icon: Tv, label: "Canlı TV", shortLabel: "TV" },
  { path: "/app/movies", icon: Film, label: "Filmlər", shortLabel: "Filmlər" },
  { path: "/app/series", icon: Clapperboard, label: "Seriallar", shortLabel: "Seriallar" },
  { path: "/app/profile", icon: User, label: "Profil", shortLabel: "Profil" },
];

export default function AppLayout() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => initWebPush(), 3000);
    return () => clearTimeout(t);
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#09090b] text-white flex">
      {/* Sidebar — Desktop only (≥768px) */}
      <aside 
        className={`hidden md:flex sticky top-0 h-screen z-50 bg-black flex-col items-center py-8 transition-all duration-300 flex-shrink-0 ${sidebarOpen ? 'w-[220px]' : 'w-20'}`}
        onMouseEnter={() => setSidebarOpen(true)}
        onMouseLeave={() => setSidebarOpen(false)}
      >
        {/* Logo */}
        <div className="relative w-full flex items-center justify-center mb-10 h-14">
          <img 
            src="/fibernet-logo.png" 
            alt="Fibernet TV" 
            className={`w-12 h-12 object-contain transition-opacity duration-150 ${sidebarOpen ? 'opacity-0 absolute' : 'opacity-100'}`}
          />
          <img 
            src="/fibernet-logo-full.png" 
            alt="Fibernet TV" 
            className={`h-12 object-contain transition-opacity duration-150 ${sidebarOpen ? 'opacity-100' : 'opacity-0 absolute'}`}
          />
        </div>

        {/* Navigation */}
        {navItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.end}
            className={({ isActive }) =>
              `relative rounded-r-2xl mb-4 flex items-center transition-all duration-300 overflow-hidden ${
                sidebarOpen ? 'w-52 justify-start px-5 h-14' : 'w-[68px] h-14 justify-center'
              } ${
                isActive
                  ? "bg-white/10 backdrop-blur-md text-white"
                  : "text-zinc-600 hover:text-white hover:bg-white/5"
              }`
            }
          >
            {({ isActive }) => (
              <>
                {isActive && (
                  <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-amber-500" />
                )}
                <item.icon className="w-5 h-5 flex-shrink-0" />
                {sidebarOpen && (
                  <span className="ml-4 text-sm font-medium whitespace-nowrap">{item.label}</span>
                )}
              </>
            )}
          </NavLink>
        ))}

        {/* Spacer */}
        <div className="flex-1" />

        {/* Logout */}
        <button
          onClick={handleLogout}
          className={`rounded-r-2xl mb-4 flex items-center transition-all duration-300 ${
            sidebarOpen ? 'w-52 justify-start px-5 h-14' : 'w-[68px] h-14 justify-center'
          } text-zinc-600 hover:text-white hover:bg-white/5`}
        >
          <LogOut className="w-5 h-5 flex-shrink-0" />
          {sidebarOpen && (
            <span className="ml-4 text-sm font-medium whitespace-nowrap">Çıxış</span>
          )}
        </button>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-hidden pb-16 md:pb-0">
        {/* Page Content */}
        <div>
          <Routes>
            <Route index element={<HomePage />} />
            <Route path="live" element={<LiveTVPage />} />
            <Route path="movies" element={<MoviesPage />} />
            <Route path="series" element={<SeriesPage />} />
            <Route path="profile" element={<ProfilePage />} />
            <Route path="qr-scan" element={<QrScanPage />} />
            <Route path="player/:type/:id" element={<PlayerPage />} />
          </Routes>
        </div>
      </main>

      {/* Bottom Tab Bar — Mobile only (<768px) */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-black/95 backdrop-blur-md border-t border-zinc-800 flex items-center justify-around h-16 px-1">
        {navItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.end}
            className={({ isActive }) =>
              `relative flex flex-col items-center justify-center flex-1 h-full transition-colors ${
                isActive ? "text-amber-500" : "text-zinc-500 hover:text-white"
              }`
            }
            data-testid={`mobile-nav-${item.shortLabel.toLowerCase()}`}
          >
            {({ isActive }) => (
              <>
                {isActive && (
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-12 h-[2px] bg-amber-500 rounded-t-full" />
                )}
                <item.icon className="w-5 h-5" />
                <span className="text-[10px] mt-1 font-medium">{item.shortLabel}</span>
              </>
            )}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
